import { Controller, useForm } from "react-hook-form";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  Box,
  ButtonGroup,
  Tag,
} from "@chakra-ui/react";
import { db } from "firebaseConfig";
import { useForfait } from "hooks/useForfait";
import { useClient } from "hooks/useClient";
import { ChangeEvent, useEffect, useState } from "react";
import { useIntervention } from "hooks/useIntervention";
import { useSitesDuForfait } from "hooks/useSitesDuForfait";
import UserIntervention from "components/user/UserIntervention";
import InterventionSites from "components/forms/InterventionSites";
import moment from "moment";
import { champsSitesIntervention, nomsDesSites } from "utils";

interface InterventionFormProps {
  onCloseModal: () => void;
  newData: (data: InterventionType[]) => void;
  updateData: InterventionType[];
}

export default function InterventionForm(props: InterventionFormProps) {
  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<InterventionType>();

  const [clients, setClients] = useState<ClientForfaitType[] | null>(null);
  const [selectedIdForfait, setSelectedIdForfait] = useState<{
    id: string;
    temps: number;
  }>();
  const { fetch } = useIntervention();
  const { getClientsByForfait } = useClient();
  const { getCurrentForfaits, getForfaitField } = useForfait();
  const [selectedClientForfait, setSelectedClientForfait] =
    useState<string>("");
  const choix = useSitesDuForfait(selectedClientForfait, props.updateData?.[0]);
  // Les sites du forfait sont cochés par défaut, et se décochent
  useEffect(() => {
    if (choix.pret) {
      setValue("idsites", choix.parDefaut);
      setValue("cms", choix.cmsParDefaut);
    }
  }, [choix.pret, choix.parDefaut, choix.cmsParDefaut, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentForfaits: CurrentForfaits[] = await fetchForfaits(); // Attendre que fetchForfaits soit résolu
        // Maintenant, vous pouvez utiliser currentForfaits pour effectuer d'autres opérations si nécessaire
        if (currentForfaits) {
          const currentClients = await fetchClients(currentForfaits);
          setClients(currentClients);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchForfaits = async () => {
      try {
        const data = await getCurrentForfaits();
        return data; // Renvoyer les données pour les utiliser ultérieurement
      } catch (error) {
        console.error(error);
      }
    };

    const fetchClients = async (currentForfaits: CurrentForfaits[]) => {
      try {
        const data = await getClientsByForfait(currentForfaits);
        return data;
      } catch (error) {
        console.error(error);
      }
    };

    fetchData(); // Appeler la fonction principale
    if(props.updateData?.[0]?.idforfait){
      setValue("idforfait",props.updateData[0].idforfait);
      setSelectedClientForfait(props.updateData[0].idforfait);
    }
  }, [getClientsByForfait, getCurrentForfaits, props.updateData, setValue]);

  const handleSelectChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientForfait(event.target.value);
    setValue("idforfait", event.target.value);
    const tempsAsString = await getForfaitField(event.target.value, "temps");
    const temps = parseFloat(tempsAsString);
    setSelectedIdForfait({ id: event.target.value, temps: temps });

    // Récupérer et pré-remplir le site_internet du forfait
    const siteInternet = await getForfaitField(event.target.value, "site_internet");
    if (siteInternet) {
      setValue("site_internet", siteInternet, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    }
  };

  async function onSubmit(values: InterventionType) {
    try {
      const dateIntervention =
        typeof values.date_intervention === "string"
          ? Timestamp.fromDate(new Date(values.date_intervention))
          : values.date_intervention;

      const envoiEmail = Boolean(values.envoi_email);
      const plugins = Boolean(values.plugins);
      const sauvegarde = Boolean(values.sauvegarde);
      const { idsites, site_internet, cms, ...others } = values;
      const newValues = {
        ...others,
        ...champsSitesIntervention({ idsites, site_internet, cms }, choix.sites, choix.parDefaut, props.updateData !== null),
        date_intervention: dateIntervention,
        envoi_email: envoiEmail,
        plugins: plugins,
        sauvegarde: sauvegarde,
      };
      if (props.updateData === null) {
        await addDoc(collection(db, "intervention"), newValues);
      } else {
        const updateForfait = doc(db, "intervention", props.updateData[0].id);
        await updateDoc(updateForfait, newValues);
      }
      props.onCloseModal();
      const newData: InterventionType[] = await fetch();
      props.newData(newData);
    } catch (error) {
      console.error("Error adding data to Firebase:", error);
    }
  }
  const getUpdateDataValue = (field: keyof InterventionType) => {
    return props.updateData !== null ? props.updateData[0][field] : "";
  };

  const formatDateIntervention = () => {
    let dateIntervention;
    if(props.updateData){
      if (props.updateData[0].date_intervention instanceof Date) {
        dateIntervention = moment(props.updateData[0].date_intervention);
      } else {
        dateIntervention = moment(
          Number(props.updateData[0].date_intervention.seconds) * 1000 +
            Number(props.updateData[0].date_intervention.nanoseconds) / 1000000
        );
      }
      return dateIntervention.format("YYYY-MM-DDTHH:mm");
    }else{
      // Retourner la date et heure actuelle par défaut
      return moment().format("YYYY-MM-DDTHH:mm");
    }
  };

  const renderInput = (
    field: keyof InterventionType,
    placeholder: string,
    minLength: number,
    typeInput: string
  ) => {
    const defaultValue = getUpdateDataValue(field);
    const maxNumber = 30;
    const defaultValueForInput =
      typeInput === "datetime-local" ? formatDateIntervention() : defaultValue;

    if (typeInput === "select") {
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <FormLabel htmlFor={field}>{placeholder}</FormLabel>
          {selectedIdForfait && (
            <Box mb={2}>
              <UserIntervention
                id={selectedIdForfait.id}
                temps={selectedIdForfait.temps}
              />
            </Box>
          )}
          <Controller
            name="idforfait"
            control={control}
            defaultValue={props.updateData?.[0]?.idforfait}
            render={({ field: { ref, value, onChange, ...field } }) => {
              return (
                <Select
                  placeholder="Client"
                  {...register("idforfait", {
                    required: "Champ obligatoire",
                    // ... autres règles de validation
                  })}
                  {...field}
                  ref={ref}
                  value={selectedClientForfait}
                  onChange={handleSelectChange}
                >
                  {/* Générez les options à partir des données des clients */}
                  {clients &&
                    clients.map((client) => (
                      <option key={client.idforfait} value={client.idforfait}>
                        {[client.raison_sociale, nomsDesSites(client.idsites, choix.tous) || client.site_internet]
                          .filter(Boolean)
                          .join(" — ")}
                      </option>
                    ))}
                </Select>
              );
            }}
          />
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      );
    } else if (typeInput === "number") {
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <FormLabel htmlFor={field}>{placeholder}</FormLabel>
          <Controller
            name={field as "temps_passe"}
            control={control}
            defaultValue={Number(defaultValue) || 0}
            rules={{
              required: "Champ obligatoire",
              min: {
                value: 0.1,
                message: "Le temps doit être supérieur à 0"
              }
            }}
            render={({ field: { onChange, value, name, ref } }) => (
              <NumberInput
                name={name}
                value={value}
                max={maxNumber}
                clampValueOnBlur={false}
                onChange={(_, valueNumber) => onChange(valueNumber)}
              >
                <NumberInputField ref={ref} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            )}
          />
          {/* Suggestions de temps rapides */}
          {field === "temps_passe" && (
            <ButtonGroup size="sm" mt={2} spacing={2}>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 0.5, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                0.5h
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 0.75, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                0.75h
              </Button>
              <Button
                variant="outline"
                colorScheme="teal"
                onClick={() => setValue("temps_passe", 1, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                })}
              >
                1h
              </Button>
            </ButtonGroup>
          )}
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      );
    } else if (typeInput === "boolean") {
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <Checkbox
            name={field}
            {...register(field)}
            defaultChecked={defaultValue as boolean}
          >
            {field}
          </Checkbox>
        </FormControl>
      );
    } else {
      // Utilisez le bloc else pour gérer d'autres types d'entrées
      return (
        <FormControl isInvalid={!!errors[field]} mb={4} key={field}>
          <FormLabel htmlFor={field}>{placeholder}</FormLabel>
          <Input
            id={field}
            placeholder={placeholder}
            {...register(field, {
              required: "Champ obligatoire",
              minLength: {
                value: minLength,
                message: `La longueur minimum est de ${minLength} caractères`,
              },
            })}
            defaultValue={defaultValueForInput as string}
            type={typeInput}
          />
          {/* Suggestion CMS WordPress */}
          {field === "cms" && (
            <Tag
              mt={2}
              size="md"
              colorScheme="teal"
              cursor="pointer"
              onClick={() => setValue("cms", "WordPress", {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
              })}
              _hover={{ opacity: 0.8 }}
            >
              WordPress
            </Tag>
          )}
          <FormErrorMessage>
            {errors[field] && errors[field].message}
          </FormErrorMessage>
        </FormControl>
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {renderInput(
        "date_intervention",
        "Date d'intervention",
        4,
        "datetime-local"
      )}
      {renderInput("idforfait", "Client", 4, "select")}
      {renderInput("temps_passe", "Temps", 2, "number")}
      <InterventionSites choix={choix} control={control} errors={errors} setValue={setValue} />
      {choix.pret && choix.sites.length === 0 && renderInput("site_internet", "Site internet", 4, "text")}
      {choix.pret && choix.sites.length === 0 && renderInput("cms", "CMS", 4, "text")}
      {renderInput("envoi_email", "Email", 1, "boolean")}
      {renderInput("plugins", "Plugins", 1, "boolean")}
      {renderInput("sauvegarde", "Sauvegarde", 1, "boolean")}

      <Button
        mt={4}
        colorScheme="teal"
        isLoading={isSubmitting}
        type="submit"
        mb={4}
      >
        {props.updateData !== null
          ? `Modifier l'intervention`
          : `Ajouter une intervention`}
      </Button>
    </form>
  );
}
